import { Link } from "@tanstack/react-router";
import { useCallback, useMemo, useState } from "react";

import { useCalendar } from "../api/hooks/useCalendar";
import { type ListNotesQuery, useNotes } from "../api/hooks/useNotes";
import { useRestoreNote } from "../api/hooks/useRestoreNote";
import type { AnalyticsFilters } from "../api/hooks/useSummary";
import { useTags } from "../api/hooks/useTags";
import { useToggleFavoriteNote } from "../api/hooks/useToggleFavoriteNote";
import { formatTradePickerLabel } from "../api/hooks/useTradePicker";
import { useTrades } from "../api/hooks/useTrades";
import type { components } from "../api/schema";
import { CalendarSummary } from "../components/journal/CalendarSummary";
import { JournalTimeline } from "../components/journal/JournalTimeline";
import { MiniCalendar, type DayPnl } from "../components/journal/MiniCalendar";
import { NoteSidePanel, type NotePanelState } from "../components/journal/NoteSidePanel";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";
import { cn } from "../lib/cn";
import {
  buildJournalDays,
  isoMonthKey,
  monthBounds,
  monthCursorFromNow,
  monthCursorKey,
  noteAnchorDate,
  shiftMonth,
  todayIso,
  type DayGroup,
  type MonthCursor,
} from "../lib/journal";
import { formatSignedMoney } from "../lib/money";

type NoteResponse = components["schemas"]["NoteResponse"];
type NoteSortKey = components["schemas"]["NoteSortKey"];
type SortOrder = components["schemas"]["SortOrder"];
type TagResponse = components["schemas"]["TagResponse"];

type TypeFilter = "all" | "trade" | "trading_day" | "other" | "favorites";

const TYPE_FILTERS: ReadonlyArray<{ value: TypeFilter; label: string }> = [
  { value: "all", label: "All" },
  { value: "trade", label: "Trade" },
  { value: "trading_day", label: "Trading day" },
  { value: "other", label: "Other" },
  { value: "favorites", label: "Favorites" },
];

interface SortOption {
  value: string;
  label: string;
  key: NoteSortKey;
  order: SortOrder;
}

const DEFAULT_SORT: SortOption = {
  value: "created_desc",
  label: "Newest first",
  key: "created_at",
  order: "desc",
};

const SORT_OPTIONS: ReadonlyArray<SortOption> = [
  DEFAULT_SORT,
  { value: "created_asc", label: "Oldest first", key: "created_at", order: "asc" },
  { value: "updated_desc", label: "Recently updated", key: "updated_at", order: "desc" },
];

// Upper bound on the month's trades fetched only to label trade notes with their
// "SYMBOL · date · P&L"; a trade note whose trade falls outside this window keeps
// its short-id fallback (ADR 0036 D7).
const MONTH_TRADES_LIMIT = 200;

function buildListQuery(
  typeFilter: TypeFilter,
  tagId: string,
  search: string,
  includeDeleted: boolean,
  sortKey: NoteSortKey,
  sortOrder: SortOrder,
): ListNotesQuery {
  const query: ListNotesQuery = { sort_key: sortKey, sort_order: sortOrder };
  if (includeDeleted) query.include_deleted = true;
  if (typeFilter === "favorites") {
    query.favorited = true;
  } else if (typeFilter !== "all") {
    query.note_type = typeFilter;
  }
  if (tagId) query.tag_id = tagId;
  const trimmed = search.trim();
  if (trimmed) query.search = trimmed;
  return query;
}

function downloadJson(filename: string, payload: unknown): void {
  const json = JSON.stringify(payload, null, 2);
  const blob = new Blob([json], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}

interface TagLookup {
  byId: Map<string, TagResponse>;
}

function tagDisplayName(lookup: TagLookup, tagId: string): string {
  const tag = lookup.byId.get(tagId);
  if (tag === undefined) return tagId.slice(0, 8);
  return tag.is_deleted ? `${tag.name} (deleted)` : tag.name;
}

function cursorFromIso(iso: string): MonthCursor {
  const parts = iso.split("-");
  return { year: Number(parts[0]), month: Number(parts[1]) - 1 };
}

export function NotesPage() {
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState<TypeFilter>("all");
  const [tagFilter, setTagFilter] = useState("");
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [sortValue, setSortValue] = useState<string>(DEFAULT_SORT.value);
  // null = follow the latest note's month; a value pins the view to a month the
  // user navigated to with the chevrons.
  const [manualCursor, setManualCursor] = useState<MonthCursor | null>(null);
  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const [panelState, setPanelState] = useState<NotePanelState | null>(null);

  const sort = SORT_OPTIONS.find((o) => o.value === sortValue) ?? DEFAULT_SORT;
  const listQuery = useMemo(
    () => buildListQuery(typeFilter, tagFilter, search, includeDeleted, sort.key, sort.order),
    [typeFilter, tagFilter, search, includeDeleted, sort.key, sort.order],
  );

  const notesQuery = useNotes(listQuery);
  const tagsQuery = useTags({ include_deleted: true });
  const favoriteMutation = useToggleFavoriteNote();
  const restoreMutation = useRestoreNote();

  const notes = useMemo(() => notesQuery.data?.items ?? [], [notesQuery.data]);

  // Open the journal on the month of the most recent note so the timeline is not
  // empty on arrival; fall back to the current month when there are no notes.
  const autoCursor = useMemo<MonthCursor>(() => {
    let latest: string | null = null;
    for (const note of notes) {
      const anchor = noteAnchorDate(note);
      if (latest === null || anchor > latest) latest = anchor;
    }
    return latest === null ? monthCursorFromNow(new Date()) : cursorFromIso(latest);
  }, [notes]);
  const cursor = manualCursor ?? autoCursor;
  const monthKey = monthCursorKey(cursor);

  const monthRange = useMemo(() => monthBounds(cursor), [cursor]);
  const calendarFilters = useMemo<AnalyticsFilters>(
    () => ({ since: monthRange.since, until: monthRange.until }),
    [monthRange],
  );
  const calendarQuery = useCalendar(calendarFilters);

  // The visible month's trades, fetched only to label trade notes (D7) with the
  // shared "SYMBOL · date · P&L" picker format.
  const monthTradesQuery = useTrades({
    opened_after: monthRange.since,
    opened_before: monthRange.until,
    limit: MONTH_TRADES_LIMIT,
  });
  const tradeLabelById = useMemo(() => {
    const map = new Map<string, string>();
    for (const trade of monthTradesQuery.data?.items ?? []) {
      const pnl = trade.realized_pnl_amount;
      map.set(
        trade.trade_id,
        formatTradePickerLabel({
          symbol: trade.primary_symbol ?? "",
          openedDate: trade.opened_at != null ? trade.opened_at.slice(0, 10) : "—",
          pnl: pnl != null ? formatSignedMoney(pnl, { currency: trade.currency }) : "—",
        }),
      );
    }
    return map;
  }, [monthTradesQuery.data]);
  const tradeLabelOf = useCallback(
    (note: NoteResponse): string | null =>
      note.trade_id !== null ? (tradeLabelById.get(note.trade_id) ?? null) : null,
    [tradeLabelById],
  );

  const dayPnl = useMemo(() => {
    const map = new Map<string, DayPnl>();
    for (const point of calendarQuery.data?.points ?? []) {
      map.set(point.trade_date, {
        net_pnl_amount: point.net_pnl_amount,
        trade_count: point.trade_count,
      });
    }
    return map;
  }, [calendarQuery.data]);
  const currency = calendarQuery.data?.currency ?? "USD";

  const tagLookup = useMemo<TagLookup>(() => {
    const byId = new Map<string, TagResponse>();
    for (const tag of tagsQuery.data?.items ?? []) byId.set(tag.tag_id, tag);
    return { byId };
  }, [tagsQuery.data]);

  const activeTagOptions = useMemo(
    () => (tagsQuery.data?.items ?? []).filter((tag) => !tag.is_deleted),
    [tagsQuery.data],
  );

  // Notes anchored in the visible month, then optionally narrowed to a clicked
  // calendar day. The calendar/month nav is a true client-side filter (the API
  // has no month parameter).
  const monthNotes = useMemo(
    () => notes.filter((note) => isoMonthKey(noteAnchorDate(note)) === monthKey),
    [notes, monthKey],
  );
  const visibleNotes = useMemo(
    () =>
      selectedDay === null
        ? monthNotes
        : monthNotes.filter((note) => noteAnchorDate(note) === selectedDay),
    [monthNotes, selectedDay],
  );

  // The trading days in the visible month (every day the user traded, from the
  // analytics calendar) — D7 lists these even when they carry no note yet.
  const monthTradeDays = useMemo(
    () =>
      (calendarQuery.data?.points ?? [])
        .map((point) => point.trade_date)
        .filter((date) => isoMonthKey(date) === monthKey),
    [calendarQuery.data, monthKey],
  );

  // Day-grouped spine over the UNION of note days and trade days (D7). When a
  // day is selected, the spine narrows to just that day.
  const groups = useMemo<DayGroup<NoteResponse>[]>(() => {
    const days = selectedDay !== null ? [selectedDay] : monthTradeDays;
    return buildJournalDays(visibleNotes, noteAnchorDate, days);
  }, [visibleNotes, monthTradeDays, selectedDay]);

  const noteDays = useMemo(
    () => new Set(monthNotes.map((note) => noteAnchorDate(note))),
    [monthNotes],
  );
  const favoriteCount = useMemo(
    () => monthNotes.filter((note) => note.favorited).length,
    [monthNotes],
  );
  const today = useMemo(() => todayIso(new Date()), []);

  function handlePrevMonth(): void {
    setManualCursor(shiftMonth(cursor, -1));
    setSelectedDay(null);
  }
  function handleNextMonth(): void {
    setManualCursor(shiftMonth(cursor, 1));
    setSelectedDay(null);
  }
  function handleSelectDay(isoDate: string): void {
    setSelectedDay((current) => (current === isoDate ? null : isoDate));
  }

  function handleExport(): void {
    downloadJson(`notes-${new Date().toISOString().slice(0, 10)}.json`, {
      exported_at: new Date().toISOString(),
      filters: listQuery,
      month: monthKey,
      selected_day: selectedDay,
      items: visibleNotes,
    });
  }

  function handleToggleFavorite(note: NoteResponse): void {
    favoriteMutation.mutate({ noteId: note.note_id });
  }
  function handleRestore(note: NoteResponse): void {
    restoreMutation.mutate({ noteId: note.note_id });
  }
  // Adding a note for a journaled day with no notes defaults to a trading-day
  // entry for that date; the user can switch the type in the panel.
  function handleCreateForDay(isoDate: string): void {
    setPanelState({ mode: "create", noteType: "trading_day", tradingDay: isoDate });
  }

  const isFavoriteOrRestoreBusy = favoriteMutation.isPending || restoreMutation.isPending;

  return (
    <section className="relative space-y-5 p-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl tracking-tight text-on-surface">Notes</h1>
          <p className="mt-1.5 text-[12.5px] text-on-surface-muted">
            A day-grouped journal of your trade, trading-day, and freeform notes.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/tags"
            className="inline-flex h-9 items-center rounded-control border border-outline bg-surface-container-high px-3 text-[12.5px] font-medium text-on-surface-variant hover:bg-surface-container-low"
          >
            Manage tags
          </Link>
          <Button type="button" variant="outline" onClick={handleExport} disabled={visibleNotes.length === 0}>
            Export JSON
          </Button>
          <Button
            type="button"
            className="bg-on-surface text-surface hover:opacity-90"
            onClick={() => setPanelState({ mode: "create" })}
          >
            New note
          </Button>
        </div>
      </header>

      {/* Toolbar: search, segmented type filter, tag + sort drops, show-deleted */}
      <div className="flex flex-wrap items-center gap-2.5">
        <div className="flex min-w-[220px] flex-1 items-center gap-2 rounded-control border border-outline-variant bg-surface-container-high px-3 py-2">
          <svg
            viewBox="0 0 24 24"
            aria-hidden="true"
            className="h-[15px] w-[15px] text-outline"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinecap="round"
          >
            <circle cx="11" cy="11" r="7" />
            <path d="M21 21l-4.3-4.3" />
          </svg>
          <input
            aria-label="Search"
            data-testid="notes-search-input"
            placeholder="Search note bodies…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="flex-1 border-0 bg-transparent text-[13px] text-on-surface outline-none placeholder:text-outline"
          />
        </div>

        <div
          role="radiogroup"
          aria-label="Filter by type"
          className="inline-flex rounded-control border border-outline-variant bg-surface-container p-[3px]"
        >
          {TYPE_FILTERS.map((filter) => {
            const active = typeFilter === filter.value;
            return (
              <button
                key={filter.value}
                type="button"
                role="radio"
                aria-checked={active}
                data-testid={`note-type-filter-${filter.value}`}
                onClick={() => setTypeFilter(filter.value)}
                className={cn(
                  "rounded-[6px] px-3 py-1.5 text-[12.5px] font-medium transition-colors",
                  active
                    ? "bg-primary font-semibold text-on-primary shadow-sm"
                    : "text-on-surface-muted hover:text-on-surface-variant",
                )}
              >
                {filter.label}
              </button>
            );
          })}
        </div>

        <select
          id="notes-tag-filter"
          aria-label="Tag"
          data-testid="notes-tag-filter"
          value={tagFilter}
          onChange={(e) => setTagFilter(e.target.value)}
          className="h-9 rounded-control border border-outline-variant bg-surface-container-high px-3 text-[12.5px] text-on-surface-variant"
        >
          <option value="">All tags</option>
          {activeTagOptions.map((tag) => (
            <option key={tag.tag_id} value={tag.tag_id}>
              {tag.name}
            </option>
          ))}
        </select>

        <select
          aria-label="Sort"
          data-testid="notes-sort"
          value={sortValue}
          onChange={(e) => setSortValue(e.target.value)}
          className="h-9 rounded-control border border-outline-variant bg-surface-container-high px-3 text-[12.5px] text-on-surface-variant"
        >
          {SORT_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>

        <label className="flex items-center gap-2 text-[12.5px] text-on-surface-muted">
          <input
            type="checkbox"
            className="h-4 w-4"
            checked={includeDeleted}
            onChange={(e) => setIncludeDeleted(e.target.checked)}
          />
          Show deleted
        </label>
      </div>

      <div className="grid grid-cols-1 items-start gap-[18px] lg:grid-cols-[268px_1fr]">
        <aside className="top-0 flex flex-col gap-3.5 lg:sticky">
          <MiniCalendar
            cursor={cursor}
            dayPnl={dayPnl}
            noteDays={noteDays}
            selectedDay={selectedDay}
            today={today}
            onPrev={handlePrevMonth}
            onNext={handleNextMonth}
            onSelectDay={handleSelectDay}
          />
          <CalendarSummary
            noteCount={monthNotes.length}
            favoriteCount={favoriteCount}
            daysJournaled={noteDays.size}
          />
          {selectedDay !== null ? (
            <button
              type="button"
              onClick={() => setSelectedDay(null)}
              className="rounded-control border border-outline-variant bg-surface-container px-3 py-2 text-[12px] font-medium text-on-surface-variant hover:bg-surface-container-low"
            >
              Clear day filter ({selectedDay})
            </button>
          ) : null}
        </aside>

        <div>
          {notesQuery.isLoading ? (
            <div role="status" aria-live="polite" className="flex flex-col gap-3">
              <span className="sr-only">Loading notes</span>
              {Array.from({ length: 4 }).map((_, index) => (
                <Skeleton key={index} className="h-24 w-full rounded-card" />
              ))}
            </div>
          ) : notesQuery.isError ? (
            <p role="alert" className="text-sm font-medium text-danger">
              {notesQuery.error instanceof Error
                ? notesQuery.error.message
                : "Failed to load notes."}
            </p>
          ) : groups.length === 0 ? (
            <div className="flex items-start gap-3 rounded-card border border-outline-variant bg-surface-container-high px-4 py-4">
              <span className="grid h-8 w-8 flex-none place-items-center rounded-control bg-primary-soft text-primary-hover">
                <svg
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                  className="h-[15px] w-[15px]"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={1.8}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M5 4h11l3 3v13a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1Z" />
                  <path d="M8 10h8M8 14h6" />
                </svg>
              </span>
              <div>
                <p className="text-[12.5px] font-semibold text-on-surface">
                  No notes match the current filters.
                </p>
                <p className="mt-0.5 text-[11.5px] text-on-surface-muted">
                  Try another month, clear the day filter, or start a new entry.
                </p>
              </div>
            </div>
          ) : (
            <JournalTimeline
              groups={groups}
              dayPnl={dayPnl}
              currency={currency}
              selectedDay={selectedDay}
              tagName={(tagId) => tagDisplayName(tagLookup, tagId)}
              tradeLabel={tradeLabelOf}
              isMutating={isFavoriteOrRestoreBusy}
              onOpenNote={(note) => setPanelState({ mode: "edit", note })}
              onCreateForDay={handleCreateForDay}
              onToggleFavorite={handleToggleFavorite}
              onRestore={handleRestore}
            />
          )}
        </div>
      </div>

      <NoteSidePanel
        state={panelState}
        onClose={() => setPanelState(null)}
        tagOptions={activeTagOptions}
        tagName={(tagId) => tagDisplayName(tagLookup, tagId)}
        tradeLabel={panelState?.mode === "edit" ? tradeLabelOf(panelState.note) : null}
      />
    </section>
  );
}
